import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { payload } from "./envelope";
import { ApiError, errorCode, errorMessage } from "./errors";
import type { components } from "./schema";

export type ScheduleException = components["schemas"]["ScheduleException"];

export type ScheduleExceptionInput = {
  /** Data local do estabelecimento, `YYYY-MM-DD` — dia de calendário, sem fuso. */
  date: string;
  /** `true` fecha o dia inteiro e ignora `opensAt`/`closesAt`. */
  closed: boolean;
  /** `HH:MM` local — obrigatório junto com `closesAt` quando `closed: false`. */
  opensAt?: string;
  closesAt?: string;
  /** Omitido = vale pro estabelecimento inteiro; preenchido = só esse profissional. */
  professionalId?: string;
  reason?: string;
};

/**
 * Exceções de calendário do estabelecimento (feriado, folga, horário
 * especial), do passado e do futuro, não paginada — inclui tanto as da loja
 * quanto as de profissional. Entram no cálculo real de disponibilidade: dia
 * `closed` não devolve horário em `/appointments/availability`.
 */
export function scheduleExceptionsQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "schedule-exceptions"] as const,
    queryFn: () =>
      payload(
        api.GET("/establishments/{id}/schedule-exceptions", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

/**
 * Exceção de profissional GANHA da exceção do estabelecimento na mesma data
 * (as férias do barbeiro valem mesmo com a loja aberta). `422 hours_required`
 * se `closed: false` sem `opensAt`/`closesAt`.
 */
export function createScheduleException(
  establishmentId: string,
  input: ScheduleExceptionInput,
): Promise<ScheduleException> {
  return payload(
    api.POST("/establishments/{id}/schedule-exceptions", {
      params: { path: { id: establishmentId } },
      body: {
        date: input.date,
        closed: input.closed,
        opens_at: input.opensAt,
        closes_at: input.closesAt,
        professional_id: input.professionalId,
        reason: input.reason,
      },
    }),
  );
}

/**
 * Remoção definitiva: a data volta a seguir o expediente semanal normal —
 * agendamentos JÁ criados não são tocados. 204 sem corpo.
 */
export async function deleteScheduleException(id: string): Promise<void> {
  const { error } = await api.DELETE("/schedule-exceptions/{id}", {
    params: { path: { id } },
  });
  if (error) {
    throw new ApiError(errorCode(error) ?? "unknown_error", errorMessage(error));
  }
}
